import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Store as NgrxStore } from "@ngrx/store";
import { ResourceSkillStore } from "@app/services/store/resource-skill-store";
import { BaseDataTablePageDirective } from "@app/view/shared/base-pages/base-data-table-page/base-data-table-page.directive";
import { SystemSearchMetaRestService } from "@app/services/hal/system-search-meta-rest.service";
import { UntypedFormBuilder } from "@angular/forms";
import { Router } from "@angular/router";
import { NgxUiLoaderService } from "ngx-ui-loader";
import { Store } from "@bryntum/core-thin";
import { ServiceErrorHandlerService } from "@app/view/shared/service-error-handler.service";
import { ResourceSkill } from "@app/models/rest/resource-skill";
import { Skill } from "@app/models/rest/skill";
import { ResourceFactMappingPatternStore } from "@app/services/store/resource-fact-mapping-pattern-store";
import { HateoasResourceService } from "@lagoshny/ngx-hateoas-client";
import { ClientConfigService } from "@app/core/config/client-config.service";
import { CustomAjaxStore } from "@app/view/shared/base-pages/base-data-table-page/custom-ajax-store.component";
import { ResourceToSkillMappingDialogComponent } from "./resource-to-skill-mapping-dialog.component";
import { ConfirmResourceSkillChangesDialogComponent } from "./confirm-resource-skill-changes-dialog.component";
import {
  MatCard,
  MatCardHeader,
  MatCardTitle,
  MatCardContent
} from "@angular/material/card";
import { MatButton } from "@angular/material/button";
import { MatPaginator } from "@angular/material/paginator";
import { ResourceStore } from "@app/services/store/resource-store";

import { SharedModule } from "@app/view/shared/shared.module";

@Component({
  selector: "app-resource-skill-mapping-for-skill",
  templateUrl: "./resource-skill-mapping-for-skill.component.html",
  styleUrls: ["./resource-skill-mapping-for-skill.component.scss"],
  standalone: true,
  imports: [
    SharedModule,
    MatCard,
    MatCardHeader,
    MatCardTitle,
    MatButton,
    MatCardContent,
    MatPaginator
  ]
})
export class ResourceSkillMappingForSkillComponent
  extends BaseDataTablePageDirective<ResourceSkill>
  implements OnInit
{
  resourceSkillStore: ResourceSkillStore;
  protected store: NgrxStore<any>;
  protected dialog: MatDialog;
  protected formBuilder: UntypedFormBuilder;
  protected router = inject(Router);
  private resourceStore = inject(ResourceStore);
  private resourceFactMappingPatternStore = inject(
    ResourceFactMappingPatternStore
  );
  serverErrorHandler: ServiceErrorHandlerService;
  protected ngxUiLoaderService: NgxUiLoaderService;
  private hateoasResourceService = inject(HateoasResourceService);
  private clientConfigService = inject(ClientConfigService);

  @Input() skill: Skill;
  @Output() entityChange = new EventEmitter<any>();

  public resourceListStore = new Store({
    data: []
  });

  public mappingPatternListStore = new Store({
    data: []
  });

  @Input()
  public columns: object[] = [
    {
      text: "Resource",
      field: "resource",
      flex: 2,
      renderer: ({ record }) => {
        // Handle both direct objects and ProjectionRelType
        return record.resource
          ? typeof record.resource === "string"
            ? record.resource
            : record.resource.name
          : "none";
      },
      editable: false // Resource column is not editable
    },
    {
      field: "startDate",
      text: "Start Date",
      flex: 1,
      type: "date",
      editor: {
        type: "date",
        format: "DD-MM-YYYY"
      }
    },
    {
      field: "endDate",
      text: "End Date",
      flex: 1,
      type: "date",
      editor: {
        type: "date",
        format: "DD-MM-YYYY"
      }
    },
    {
      text: "Mapping Pattern",
      field: "mappingPattern",
      flex: 2,
      renderer: ({ record }) => {
        // Handle both direct objects and ProjectionRelType
        return record.mappingPattern
          ? typeof record.mappingPattern === "string"
            ? record.mappingPattern
            : record.mappingPattern.description
          : "none";
      },
      editor: {
        type: "dropdown",
        store: this.mappingPatternListStore,
        displayField: "description",
        valueField: null
      }
    }
  ];

  // Loading indicator for related entities
  public loadingRelatedEntities = false;

  constructor() {
    const systemSearchMetaRestService = inject(SystemSearchMetaRestService);
    const resourceSkillStore = inject(ResourceSkillStore);
    const store = inject<NgrxStore<any>>(NgrxStore);
    const dialog = inject(MatDialog);
    const formBuilder = inject(UntypedFormBuilder);
    const serverErrorHandler = inject(ServiceErrorHandlerService);
    const ngxUiLoaderService = inject(NgxUiLoaderService);

    super(
      systemSearchMetaRestService,
      resourceSkillStore,
      store,
      dialog,
      "ResourceSkill",
      formBuilder,
      serverErrorHandler,
      ngxUiLoaderService
    );
    this.resourceSkillStore = resourceSkillStore;
    this.store = store;
    this.dialog = dialog;
    this.formBuilder = formBuilder;
    this.serverErrorHandler = serverErrorHandler;
    this.ngxUiLoaderService = ngxUiLoaderService;
    const hateoasResourceService = this.hateoasResourceService;
    const clientConfigService = this.clientConfigService;

    // Set the required services on the CustomAjaxStore class
    CustomAjaxStore.setClientConfigService(clientConfigService);
    CustomAjaxStore.setHateoasResourceService(hateoasResourceService);
  }

  public get pristine() {
    return (
      !this.gridStore.changes ||
      (this.gridStore.changes.added.length === 0 &&
        this.gridStore.changes.removed.length === 0 &&
        this.gridStore.changes.modified.length === 0)
    );
  }

  ngOnInit() {
    // Load resources for dropdown
    this.resourceStore.loadAll().then((value) => {
      this.resourceListStore.add(value.resources);
    });

    // Load mapping patterns for dropdown
    this.resourceFactMappingPatternStore.loadAll().then((value) => {
      // Add a "None" option to allow clearing the mapping
      const noneOption = {
        id: null,
        description: "None"
      };
      const filtered = value.resources.filter((r) => r.description !== "None");
      this.mappingPatternListStore.add([noneOption, ...filtered]);
    });

    super.ngOnInit();

    // Set autoCommit to false before loading data
    this.gridStore.autoCommit = false;

    // Set up filter for skill and detail projection
    if (this.skill) {
      this.gridStore.fixedFilters = [
        {
          property: "skill.id",
          operator: "Equals",
          value: this.skill.id
        }
      ];

      // Pass projection directly in the load parameters
      this.gridStore.load({
        params: {
          projection: "detail"
        }
      });
    }
  }

  addRow() {
    // Open dialog to select resource and configure mapping
    const dialogRef = this.dialog.open(ResourceToSkillMappingDialogComponent, {
      width: "600px",
      data: {
        skill: this.skill,
        mappingPatterns: this.mappingPatternListStore.records
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.action === "save") {
        // Insert the new resource skill mapping from the dialog
        this.gridStore.insert(0, result.entry);
      }
    });
  }

  commit() {
    // Show confirmation dialog before saving changes
    const dialogRef = this.dialog.open(
      ConfirmResourceSkillChangesDialogComponent,
      {
        width: "500px"
      }
    );

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        super.commit();
        this.entityChange.emit();
      }
    });
  }
}
